import { ChevronLeftIcon, ChevronRightIcon, RefreshCwIcon } from "lucide-react";
import { memo, type ReactNode } from "react";
import Tooltip from "src/components/Tooltip";
import { Button } from "src/components/ui/button";
import { useDesktopNavigation } from "src/hooks/useDesktopNavigation";
import { useWindowDragRegion } from "src/hooks/useWindowDragRegion";
import { isTauriRuntime } from "src/lib/runtime/environment";
import { cn } from "src/utilities/shadcnUtils";

interface DesktopNavigationControlsProps {
  className?: string;
}

export const DesktopNavigationControls = memo(function DesktopNavigationControls({
  className,
}: DesktopNavigationControlsProps) {
  const { isAvailable, canGoBack, canGoForward, goBack, goForward } = useDesktopNavigation();

  if (!isAvailable) {
    return null;
  }

  return (
    <nav className={cn("flex items-center gap-2", className)} aria-label="Desktop navigation">
      <Tooltip text="Back (Cmd+[)" place="bottom">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          iconPlacement="only"
          onClick={goBack}
          disabled={!canGoBack}
          aria-label="Back"
        >
          <ChevronLeftIcon />
        </Button>
      </Tooltip>
      <Tooltip text="Forward (Cmd+])" place="bottom">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          iconPlacement="only"
          onClick={goForward}
          disabled={!canGoForward}
          aria-label="Forward"
        >
          <ChevronRightIcon />
        </Button>
      </Tooltip>
      <Tooltip text="Refresh" place="bottom">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          iconPlacement="only"
          onClick={() => window.location.reload()}
          aria-label="Refresh"
        >
          <RefreshCwIcon className="h-3/4" />
        </Button>
      </Tooltip>
    </nav>
  );
});

export const DesktopNavigationHeader = memo(function DesktopNavigationHeader({
  children,
}: {
  children: ReactNode;
}) {
  const dragRegion = useWindowDragRegion();

  if (!isTauriRuntime()) {
    return <div className="min-w-0 [&>*]:!py-1">{children}</div>;
  }

  return (
    <div className="flex min-w-0 select-none border-b-2 border-solid border-border bg-card" {...dragRegion}>
      <div className="w-[84px] shrink-0" aria-hidden="true" />
      <DesktopNavigationControls className="shrink-0 px-3" />
      <div className="min-w-0 flex-1 [&>*]:border-b-0 [&>*]:!py-1 [&>*]:!pl-2 [&>*]:!pr-3">{children}</div>
    </div>
  );
});
